import { NETWORK, suggestBrandFor, type Brand, type BrandKey } from "@/lib/network";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";

// Full "Our Transportation Network" section — for About page and homepage.
export function OurNetwork({
  eyebrow = "Our transportation network",
  heading = "One trusted company. Specialized brands for every journey.",
  intro,
  showBrandLinks = true,
  linkedBrandKeys,
}: {
  eyebrow?: string;
  heading?: string;
  intro?: string;
  /** When false, brand cards render without outbound links (keeps external-link count low). */
  showBrandLinks?: boolean;
  /**
   * Optional allowlist of brand keys that may render an outbound link.
   * Brands outside the list render their domain as plain text, which keeps the
   * page's external-link count within SEO audit thresholds.
   */
  linkedBrandKeys?: BrandKey[];
}) {
  const linkAllowed = (key: BrandKey) =>
    showBrandLinks && (!linkedBrandKeys || linkedBrandKeys.includes(key));
  return (
    <section
      className="border-y border-border bg-surface"
      aria-labelledby="our-network-heading"
    >
      <div className="container-x py-16 sm:py-20">
        <p className="eyebrow mb-3 text-gold">{eyebrow}</p>
        <h2
          id="our-network-heading"
          className="text-3xl md:text-4xl mb-4 text-balance max-w-3xl"
        >
          {heading}
        </h2>
        <p className="text-muted-foreground max-w-3xl mb-10 sm:mb-12 leading-relaxed">
          {intro ??
            "Premier Executive Chauffeur is one of several specialized websites operated by Black Chauffeur Limo Service. Each site serves a different audience and search intent — so you always land on the right expertise for the trip you're planning."}
        </p>

        <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {NETWORK.map((b) => (
            <li key={b.key}>
              <article className="group relative flex h-full flex-col border border-border bg-background p-6 hover:border-gold transition-colors">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <p className="font-display text-lg text-foreground group-hover:text-gold transition-colors">
                    {b.name}
                  </p>
                  {linkAllowed(b.key) && (
                    <ArrowUpRight
                      className="h-4 w-4 text-gold shrink-0 mt-1"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <p className="text-xs uppercase tracking-widest text-gold-soft mb-3">
                  {b.tagline}
                </p>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {b.purpose}
                </p>
                <div className="mt-auto pt-4 border-t border-border/60 space-y-2">
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Best for
                  </p>
                  <p className="text-xs text-foreground/80 leading-relaxed">
                    {b.bestFor}
                  </p>
                  {linkAllowed(b.key) ? (
                    <a
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-[11px] text-gold hover:text-gold-soft pt-2 before:absolute before:inset-0 before:content-['']"
                    >
                      Visit {b.host}<span className="sr-only"> — {b.tagline}</span> →
                    </a>
                  ) : (
                    <p className="pt-2 text-[11px] uppercase tracking-widest text-gold-soft">
                      {b.host}
                    </p>
                  )}
                </div>
              </article>
            </li>
          ))}
        </ul>

        {(!showBrandLinks || linkedBrandKeys) && (
          <p className="mt-8 text-sm text-muted-foreground">
            <Link
              to="/about"
              className="text-gold underline underline-offset-4"
            >
              Read how our network brands work together
            </Link>{" "}
            — each site is operated by the same dispatch team, chauffeurs, and fleet.
          </p>
        )}
      </div>
    </section>
  );
}

// Compact contextual suggestion — appears only when a sibling brand genuinely
// matches the visitor's page context (e.g. SFO airport page → SFO Ground
// Transportation). Placed inline within a service/airport page.
export function NetworkSuggestion({
  context,
  className = "",
}: {
  context: { type: "service" | "airport" | "fbo" | "city" | "corporate" | "founder"; slug?: string };
  className?: string;
}) {
  const brand = suggestBrandFor(context);
  if (!brand) return null;
  return <NetworkSuggestionCard brand={brand} className={className} />;
}


export function NetworkSuggestionCard({
  brand,
  className = "",
}: {
  brand: Brand;
  className?: string;
}) {
  return (
    <aside
      className={`border border-gold/40 bg-background p-5 sm:p-6 ${className}`}
      aria-label={`Related brand — ${brand.name}`}
    >
      <p className="eyebrow text-gold-soft mb-2">From our network</p>
      <p className="font-display text-lg text-foreground mb-1">{brand.name}</p>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
        {brand.purpose}
      </p>
      <a
        href={brand.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold-soft"
      >
        Continue on {brand.host}
        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
      </a>
    </aside>
  );
}
